import { useMemo, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { U2000InputTagType } from "../../formControls/input/U2000InputTag";
import U2000BasicTableListRow from "./U2000BasicTableListRow";
// import U2000ErrorTooltip from "../../formControls/feedback/U2000ErrorTooltip";
import U2000Alert from "../../controls/U2000Alert";
import { FaSortAlphaUp, FaSortAlphaDown } from "react-icons/fa";
// import type { FieldParameter, SortedFieldParameter } from "./U2000BasicTable";
import type { U2000GridStateHandlers } from "../../../reducer/formReducer/U2000GridStateHandlers";
import { U2000IdHeaderCheckedBoxDisplay } from "../display/U2000IdHeaderCheckedBoxDisplay";
import { U2000IdCheckedBoxDisplay } from "../display/U2000IdCheckedBoxDisplay";

// Types for columns, fields, and data rows

export const U2000IdColumnDef: U2000TableColumn = {
  name: "id",
  label: "",
  widthClass: "w-[40px] min-w-[40px] max-w-[40px]",
  noTruncate: true,
  type: "number",
  align: "center",
  headerDisplay: (column: any, label: string, allRowSelected: boolean, handleSelectAllRow?: (isSelected: boolean) => void) => {
    return <U2000IdHeaderCheckedBoxDisplay column={column} label={label} disabled={false} allRowSelected={allRowSelected} handleSelectAllRow={handleSelectAllRow} />;
  },
  display: (column: any, value: any, row: any, allowRowSelection: boolean, rowUpdated?: (row: any) => void) => {
    return <U2000IdCheckedBoxDisplay column={column} value={value} row={row} disabled={false} rowUpdated={rowUpdated} allowRowSelection={allowRowSelection} />;
  },
  isCriteriaField: false,
};

/** Option fixe pour critère recherche (ComboBox — BL-0067). */
export interface U2000CriteriaStaticOption {
  value: string;
  label: string;
  // Index signature requise pour satisfaire la contrainte générique de U2000ComboBoxBase<T extends Record<string, unknown>>
  [key: string]: unknown;
}

/** Options dynamiques depuis initLU (résolues via registre — BL-0067). */
export interface U2000CriteriaInitOptionsConfig {
  /** Clé registre, ex. "U3142/lotDetailOperationTypes" */
  optionsKey: string;
  valueKey: string;
  filterActive?: boolean;
}

export interface U2000CriteriaLookupConfig {
  /** Clé registre, ex. "U3129/findProject" */
  findMethod: string;
  /** fieldName dans le corps autocomplete { fieldName, value }[] */
  searchCriterionField: string;
  /** Nom champ API search si différent de column.name */
  criteriaField?: string;
  /** Propriété item → criteria.value (ex. "projetNumber") */
  valueKey: string;
  /** Propriétés pour libellé liste déroulante (suggestions) */
  displayKeys: string[];
  /** Propriétés pour libellé une fois sélectionné (tag, champ, critères actifs). Défaut : [valueKey] */
  selectedDisplayKeys?: string[];
  /** Filigrane du champ valeur (hint saisie). Défaut : lblSearchCriteriaLookupPlaceholder (BL-0400). */
  placeholder?: string;
  resultFields?: string[];
}

export interface U2000TableColumn extends Record<string, unknown> {
  name: string;
  label: string;
  isCriteriaField?: boolean;
  /** Autocomplete backend pour critères recherche générique (BL-0039) */
  criteriaLookup?: U2000CriteriaLookupConfig;
  /** Liste fermée pour critère (ComboBox enum / liste statique) */
  criteriaStaticOptions?: U2000CriteriaStaticOption[];
  /** Options depuis initLU via registre (ComboBox type d'opération, etc.) */
  criteriaInitOptions?: U2000CriteriaInitOptionsConfig;
  /** Filtre saisie tags critère string sans lookup (BL-0059, défaut alphanum). */
  criteriaInputTagType?: U2000InputTagType;
  isDefaultColumn?: boolean;
  isDefaultSortedColumn?: boolean;
  /** Si `isDefaultSortedColumn`, ordre initial du tri (défaut : ASC). */
  defaultSortOrder?: "ASC" | "DESC";
  type: string;
  inGridField?: boolean;
  sortable?: boolean;
  includeConditions?: string[];
  excludeConditions?: string[];
  ovverideLabelConditions?: { name: string; label: string }[];
  align?: "left" | "center" | "right";
  widthClass?: string;
  /** Si true, la cellule et l'en-tête ne sont pas tronqués (ex. colonne avec checkbox ou icône). */
  noTruncate?: boolean;
  headerDisplay?: (column: U2000TableColumn, label: string, allRowSelected: boolean, handleSelectAllRow?: (isSelected: boolean) => void) => React.ReactNode;
  display?: (column: U2000TableColumn, value: any, row: any, allRowSelected: boolean, rowUpdated?: (row: any) => void) => React.ReactNode;
}

export interface U2000TableRow {
  id: string;
  [key: string]: any;
}

interface U2000BasicTableListProps {
  gridStateHandlers: U2000GridStateHandlers<any>;
  rowClickFn?: (row: U2000TableRow) => void;
  rowUpdated?: (row: any) => void;
  handleSelectAllRow?: (isSelected: boolean) => void;
  allowRowSelection: boolean;
  /** Voir `U2000BasicTableProps.isRowSelectable`. */
  isRowSelectable?: (row: any) => boolean;
  /** Ligne(s) `<tr>` rendues dans un `<tfoot>` après le corps du tableau. */
  tableFooter?: ReactNode;
  /** Contenu personnalisé lorsque la grille est vide (remplace « Aucune donnée à afficher »). */
  emptyTableBody?: ReactNode;
  /** Tableau en largeur intrinsèque + défilement horizontal du conteneur (évite le rétrécissement des colonnes %). */
  enableHorizontalScroll?: boolean;
  /** BL-0405 — clic en-tête pour tri (colonnes `sortable`). */
  onColumnHeaderSortClick?: (column: U2000TableColumn) => void;
}

const ALIGN_CLASS: Record<string, string> = {
  left: "text-left",
  center: "text-center",
  right: "text-right",
};

const U2000BasicTableList: React.FC<U2000BasicTableListProps> = ({
  gridStateHandlers,
  rowClickFn,
  rowUpdated,
  handleSelectAllRow,
  allowRowSelection,
  isRowSelectable,
  tableFooter,
  emptyTableBody,
  enableHorizontalScroll = false,
  onColumnHeaderSortClick,
}) => {
  const { t } = useTranslation();
  // Tri par clic en-tête (BL-0405) : actif seulement si le conteneur fournit le callback (liste U2000BasicTable).
  const isHeaderSortEnabled = typeof onColumnHeaderSortClick === "function";

  const onHeaderSortKeyDown = (event: React.KeyboardEvent<HTMLTableCellElement>, column: U2000TableColumn) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    onColumnHeaderSortClick?.(column);
  };

  // Pour accéder rapidement à l'ordre de tri
  const sortedMap = useMemo(() => new Map(gridStateHandlers.getSelectedSortedFields().map((field) => [field.name, field.sortOrder])), [gridStateHandlers.getSelectedSortedFields()]);

  // Pour accéder facilement aux colonnes par nom
  const columnMap = useMemo(() => new Map(gridStateHandlers.getColumns().map((col) => [col.name, col])), [gridStateHandlers]);

  const formatValue = (column: U2000TableColumn, label: string, allRowSelected: boolean, handleSelectAllRow?: (isSelected: boolean) => void) => {
    if (typeof column.headerDisplay === "function") {
      return column.headerDisplay(column, label, allRowSelected, handleSelectAllRow);
    }
    return label;
  };

  const dataRows = gridStateHandlers.getData();
  const selectableRows = isRowSelectable ? dataRows.filter((r) => isRowSelectable(r)) : dataRows;
  const allRowSelected = selectableRows.length > 0 && selectableRows.every((row) => row["idChecked"] === true);

  return (
    <>
      {gridStateHandlers.isShowErrorsAlert() && Array.isArray(gridStateHandlers.getErrors()) && gridStateHandlers.getErrors().length > 0 && (
        <U2000Alert type="error" message={gridStateHandlers.getErrors()} isOpen={gridStateHandlers.isShowErrorsAlert()} onClose={gridStateHandlers.onHideErrorsAlert} />
      )}
      <div className={`rounded-lg border mt-2 border-gray-200 bg-white overflow-x-auto shadow-xs `}>
        <div className={enableHorizontalScroll ? "w-max min-w-full" : "w-full min-w-0 overflow-x-auto"}>
          <table
            className={
              enableHorizontalScroll
                ? "w-max min-w-full table-auto border-collapse text-sm"
                : "min-w-full w-0 table-fixed border-collapse text-sm"
            }
          >
            <caption className="sr-only">Liste des éléments</caption>
            <thead>
              <tr className="bg-gray-100 text-gray-900 border-t border-gray-300">
                {(() => {
                  const column = columnMap.get("id");
                  if (!column) return null;

                  // Si la sélection de ligne n'est pas autorisée, désactive la checkbox
                  const content = allowRowSelection ? (
                    formatValue(column, column.label, allRowSelected, handleSelectAllRow)
                  ) : (
                    <span className="opacity-50 cursor-not-allowed select-none">{formatValue(column, column.label, allRowSelected, handleSelectAllRow)}</span>
                  );

                  return (
                    <th
                      key={column.name}
                      scope="col"
                      className={`${ALIGN_CLASS[column.align ?? "left"]} px-2 py-2 border-r last:border-r-0 border-gray-200 ${column.widthClass ?? "min-w-[100px]"} whitespace-nowrap`}
                    >
                      {content ?? <span className="text-gray-400">—</span>}
                    </th>
                  );
                })()}

                {gridStateHandlers.getSelectedFields().map((fieldName: string) => {
                  const column = columnMap.get(fieldName);
                  if (!column) return null;

                  const sortOrder = sortedMap.get(column.name);
                  const hasCustomHeader = typeof column.headerDisplay === "function";
                  const headerContent = formatValue(column, column.label, allRowSelected, handleSelectAllRow);
                  const headerTruncateClass =
                    column.noTruncate || hasCustomHeader || enableHorizontalScroll
                      ? "whitespace-normal leading-snug"
                      : "truncate";
                  const isSortableByHeader = isHeaderSortEnabled && column.sortable === true;
                  const isSortBlocked = isHeaderSortEnabled && column.sortable !== true;
                  const headerTitle = isSortBlocked ? t("ttSortNotAvailable", { label: column.label }) : column.label;
                  const ariaSort = isSortableByHeader ? (sortOrder === "ASC" ? "ascending" : sortOrder === "DESC" ? "descending" : "none") : undefined;
                  const sortInteractionClass = isSortableByHeader
                    ? "cursor-pointer select-none hover:bg-gray-200/60 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-inset"
                    : isSortBlocked
                      ? "cursor-not-allowed"
                      : "";

                  return (
                    <th
                      key={column.name}
                      scope="col"
                      title={headerTitle}
                      aria-sort={ariaSort}
                      onClick={isSortableByHeader ? () => onColumnHeaderSortClick?.(column) : undefined}
                      onKeyDown={isSortableByHeader ? (event) => onHeaderSortKeyDown(event, column) : undefined}
                      tabIndex={isSortableByHeader ? 0 : undefined}
                      className={`text-sm ${ALIGN_CLASS[column.align ?? "left"]} px-4 py-2 border-r last:border-r-0 border-gray-200 ${column.widthClass ?? "min-w-[100px]"} ${headerTruncateClass} align-bottom ${sortInteractionClass}`}
                    >
                      <span className="inline-flex max-w-full items-center gap-1">
                        <span className={hasCustomHeader ? "min-w-0" : headerTruncateClass}>{headerContent ?? column.label}</span>
                        {sortOrder === "ASC" && (
                          <span className="inline-block shrink-0 text-xs text-black">
                            <FaSortAlphaDown size={16} />
                          </span>
                        )}
                        {sortOrder === "DESC" && (
                          <span className="inline-block shrink-0 text-xs text-black">
                            <FaSortAlphaUp size={16} />
                          </span>
                        )}
                      </span>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {dataRows.length === 0 ? (
                <tr>
                  <td
                    colSpan={(() => {
                      let count = gridStateHandlers.getSelectedFields().length;
                      if (columnMap.has("id")) count += 1;
                      return count;
                    })()}
                    className={emptyTableBody ? 'py-0' : 'text-center text-gray-400 py-8'}
                  >
                    {emptyTableBody ?? 'Aucune donnée à afficher'}
                  </td>
                </tr>
              ) : (
                gridStateHandlers
                  .getData()
                  .map((row, idx) => (
                    <U2000BasicTableListRow
                      key={row.id}
                      gridStateHandlers={gridStateHandlers}
                      row={row}
                      rowClickFn={rowClickFn ? (_event, r) => rowClickFn(r as U2000TableRow) : (undefined as any)}
                      even={idx % 2 === 1}
                      rowUpdated={rowUpdated}
                      allowRowSelection={allowRowSelection}
                      enableHorizontalScroll={enableHorizontalScroll}
                    />
                  ))
              )}
            </tbody>
            {tableFooter ? (
              <tfoot className="border-t-2 border-gray-200 bg-muted shadow-[0_-4px_6px_-2px_rgba(0,0,0,0.06)]">{tableFooter}</tfoot>
            ) : null}
          </table>
        </div>
      </div>
    </>
  );
};

export default U2000BasicTableList;
